import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks';

/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication.
 * Redirects to login if not authenticated.
 */

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/** Landing page for support users, and where out-of-scope URLs redirect to. */
const SUPPORT_HOME = '/accounts';

/**
 * Sibling routes under /accounts that support users may NOT reach. These are
 * business-wide analytics rather than individual account lookups.
 */
const SUPPORT_BLOCKED_ACCOUNT_ROUTES = ['overview', 'growth'];

/**
 * Whether a support user may view a path: the accounts list, or one account's
 * detail page. Mirrors the backend IsSupportOrAdmin whitelist, but this is a
 * UX redirect only — the API rejects out-of-scope calls on its own.
 */
function supportCanAccess(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === SUPPORT_HOME) return true;

  const detailMatch = path.match(/^\/accounts\/([^/]+)$/);
  return detailMatch !== null && !SUPPORT_BLOCKED_ACCOUNT_ROUTES.includes(detailMatch[1]);
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isSupportOnly, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f0f0f',
        color: '#ffffff',
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (isSupportOnly && !supportCanAccess(location.pathname)) {
    return <Navigate to={SUPPORT_HOME} replace />;
  }

  return <>{children}</>;
}

